import React, { useState } from 'react';
import classnames from 'classnames';
import CopyToClipboard from 'react-copy-to-clipboard';
import { isMobile } from 'react-device-detect';

import {
  HeartIcon,
  CommentIcon,
  ShareIcon,
  EllipsisIcon,
} from '../icons';

import styles from './EngagementRail.module.scss';

type EngagementRailProps = {
  shareUrl: string;
  shareTitle?: string;
  likeCount?: number;
  commentCount?: number;
  shareCount?: number;
};

function formatCount(n: number | undefined): string {
  if (!n || n <= 0) return '';
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  if (n < 1_000_000) return `${Math.floor(n / 1000)}K`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
}

export default function EngagementRail({
  shareUrl,
  shareTitle,
  likeCount,
  commentCount,
  shareCount,
}: EngagementRailProps) {
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    if (isMobile && typeof navigator.share === 'function') {
      try {
        await navigator.share({ url: shareUrl, title: shareTitle });
      } catch {
        // user cancelled
      }
    }
  };

  const shareButton =
    isMobile && typeof navigator !== 'undefined' && typeof navigator.share === 'function' ? (
      <button
        type="button"
        className={styles.button}
        onClick={handleShare}
        aria-label="Share"
      >
        <ShareIcon />
        {formatCount(shareCount) && (
          <span className={styles.count}>{formatCount(shareCount)}</span>
        )}
      </button>
    ) : (
      <CopyToClipboard
        text={shareUrl}
        onCopy={() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1800);
        }}
      >
        <button
          type="button"
          className={styles.button}
          aria-label={copied ? 'Link copied' : 'Copy link'}
        >
          <ShareIcon />
          <span className={styles.count}>
            {copied ? 'Copied' : formatCount(shareCount) || 'Share'}
          </span>
        </button>
      </CopyToClipboard>
    );

  return (
    <div className={styles.rail}>
      <button
        type="button"
        className={classnames(styles.button, { [styles.liked]: liked })}
        onClick={() => setLiked((v) => !v)}
        aria-label={liked ? 'Unlike' : 'Like'}
      >
        <HeartIcon filled={liked} />
        <span className={styles.count}>
          {formatCount((likeCount ?? 0) + (liked ? 1 : 0)) || ''}
        </span>
      </button>

      <button type="button" className={styles.button} aria-label="Comments" disabled>
        <CommentIcon />
        <span className={styles.count}>{formatCount(commentCount) || ''}</span>
      </button>

      {shareButton}

      <button type="button" className={styles.button} aria-label="More" disabled>
        <EllipsisIcon />
      </button>
    </div>
  );
}
